import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { get, size, identity, map, has } from 'lodash/fp';
import { keyBy, keyById } from '@mews/fp';
import { createAction } from '@mews/framework';
import { SearchType } from '@mews-commander/enums';
import { ProductServiceOrder, Service, Company, CompanyResult, Address } from '@mews-commander/data-access';
import { CommanderThunkAction, SearchResult } from '@mews-commander/types';
import { store as getStore } from '@mews-commander/legacy-app-instance';
import { createFetchAction, createErrorAction } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { updateEnterpriseProfilesState } from '@mews-commander/legacy-entities/src/ducks/enterpriseProfiles';
import { setSimpleUsers } from '@mews-commander/legacy-entities/src/ducks/simpleUsers';
import { getEnterpriseProfileId } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { getResults, getSearchResult, getSelectedSearchResultItems } from '../selectors';
import { searchNavigation } from './navigation';
import { searchResources } from './resources';
import {
    SEARCH_RESULT_SELECTED,
    SELECTED_SEARCH_RESULT_CLEARED,
    SEARCH,
    SEARCH_MORE_STARTED,
    SEARCH_MORE_SUCCEEDED,
    SEARCH_MORE_FAILED,
    SEARCH_RESULTS_CLEARED,
    SEARCH_SUCCEEDED,
    SEARCH_RESETED,
} from './actionTypes';
import { SearchFunction, SearchableComponentsName } from './types';

export const loadSearchResult = (
    { searchType, result }: { searchType: SearchType; result: Array<unknown> },
    scope: string
) => createAction(SEARCH_SUCCEEDED, [{ searchType, result }], { scope });

export const selectSearchResult = ({ selectedResult }: { selectedResult: SearchResult }, scope: string) =>
    createAction(SEARCH_RESULT_SELECTED, selectedResult, { scope });

export const clearSelectedSearchResult = (scope: string) =>
    createAction(SELECTED_SEARCH_RESULT_CLEARED, null, { scope });

export const clearSearchResults = (scope: string) => createAction(SEARCH_RESULTS_CLEARED, null, { scope });
export const resetSearch = (scope: string) => createAction(SEARCH_RESETED, null, { scope });

interface PreselectSearchResultParams {
    searchTypes: SearchType[];
    id: string;
    [key: string]: unknown;
}

export const preselectSearchResult =
    (
        { searchTypes, id, ...options }: PreselectSearchResultParams,
        scope: string
    ): CommanderThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const state = getState();

        const items = getSelectedSearchResultItems(state, { scope, searchTypes });
        const hasSearchResult = has(id, items);

        if (!hasSearchResult) {
            // if the search result is nowhere to find, pull it first, then select it;
            // TODO: create some general search request handler

            // Returned for chaining in angular
            return Promise.resolve()
                .then(async () => dispatch(search({ searchTypes, count: 2, searchTerm: id, ...options }, scope)))
                .then(() => {
                    const preselectedResult = getSearchResult(getState(), { searchTypes, id, scope });

                    if (preselectedResult) {
                        dispatch(selectSearchResult({ selectedResult: preselectedResult }, scope));
                    } else {
                        dispatch(clearSearchResults(scope));
                    }
                });
        }
        const searchResult = getSearchResult(getState(), { searchTypes, id, scope });
        dispatch(createAction(SEARCH_RESULT_SELECTED, searchResult, { scope }));

        // Returned for chaining in angular
        return Promise.resolve();
    };

interface CommonSearchParams {
    count: number;
    searchTerm: string;
    enterpriseId?: string | null;
    optionalParams?: object;
}

export interface SearchParams extends CommonSearchParams {
    searchTypes: SearchType[];
}

export const search =
    (
        { searchTypes, count, searchTerm, enterpriseId = null, optionalParams = {} }: SearchParams,
        scope: string
    ): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch, getState) => {
        const state = getState();
        const finalEnterpriseId = enterpriseId || getCurrentEnterpriseId();
        const profileId = getEnterpriseProfileId(state, { enterpriseId: finalEnterpriseId });
        const options = { startIndex: 0, count };

        const searches = map(async (type) => {
            const searchStrategy = searchTypesMap[type];
            return dispatch(searchStrategy(profileId, finalEnterpriseId, searchTerm, options, optionalParams));
        }, searchTypes);

        // TODO: fix subsequent request cancellation (by change of search term)
        const searchPromise = Promise.all(searches);
        void dispatch(createFetchAction(SEARCH, searchPromise, { scope }));
        return searchPromise;
    };

export interface SearchMoreParams extends CommonSearchParams {
    searchType: SearchType;
}

export const searchMore =
    (
        { searchType, count, searchTerm, enterpriseId = null, optionalParams }: SearchMoreParams,
        scope: string
    ): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const finalEnterpriseId = enterpriseId || getCurrentEnterpriseId();
        const profileId = getEnterpriseProfileId(state, { enterpriseId: finalEnterpriseId });

        const results = getResults(state, { scope });
        const itemsLength = size(get([searchType, 'items'], results));
        const startIndex = Math.round(itemsLength / count);

        dispatch(createAction(SEARCH_MORE_STARTED, { searchType }, { scope }));

        dispatch(
            searchTypesMap[searchType](profileId, finalEnterpriseId, searchTerm, { startIndex, count }, optionalParams)
        )
            .then((result: $TSFixMe) => {
                void dispatch(createAction(SEARCH_MORE_SUCCEEDED, result, { scope }));
            })
            .catch((e: Error) => {
                const { name, message } = e;
                const error = { name, message };
                dispatch(createErrorAction(SEARCH_MORE_FAILED, error, { searchType }, { scope }));

                throw error;
            });
    };

const enterpriseProfileResponseHandler = ({ profiles, users }: $TSFixMe, enterpriseId: $TSFixMe) => {
    const { dispatch } = getStore();

    dispatch(updateEnterpriseProfilesState(profiles, enterpriseId));
    dispatch(setSimpleUsers(users));

    const usersMap = keyById(users);
    return map(
        ({ id, data, ...other }) => ({
            ...usersMap[data.value.userId],
            ...other,
            id,
            email: data.value.email,
            isEmployee: true,
            profileId: id,
        }),
        profiles
    );
};

const reservationResponseHandler = ({ reservations, accounts }: $TSFixMe) => {
    const accountMap = keyById(accounts);

    return map(
        ({ customerId, ...other }) => ({
            ...other,
            customerId,
            customer: accountMap[customerId],
        }),
        reservations
    );
};

const companyResponseHandler = ({ companies, addresses }: CompanyResult) => {
    if (!companies) return [];
    const addressesMap: Record<string, Address> = keyBy('accountId', addresses);

    return pipe(
        companies,
        array.map(({ id, iata, ...other }) => ({
            ...other,
            id,
            address: addressesMap[id]?.isComplete ? addressesMap[id] : null,
            iata: iata || null,
        }))
    );
};

const travelAgencyResponseHandler = ({ travelAgencyContracts, companies, addresses }: CompanyResult) => {
    if (!travelAgencyContracts || !companies) return [];
    const companyMap: Record<string, Company> = keyById(companies);
    const addressesMap: Record<string, Address> = keyBy('accountId', addresses);

    return pipe(
        travelAgencyContracts,
        array.map(({ companyId, id, ...other }) => ({
            ...other,
            id: companyId,
            address: addressesMap[companyId]?.isComplete ? addressesMap[companyId] : null,
            contractId: id,
            name: companyMap[companyId].name,
            department: companyMap[companyId]?.department,
            iata: companyMap[companyId]?.iata,
        }))
    );
};

interface ProductServiceOrdersResponse {
    productServiceOrders: ProductServiceOrder[];
    services: Service[];
}

const productServiceOrderResponseHandler = ({ productServiceOrders, services }: ProductServiceOrdersResponse) => {
    const servicesMap: Record<string, Service> = keyById(services);

    return map(
        (order) => ({
            ...order,
            service: servicesMap[order.serviceId],
        }),
        productServiceOrders
    );
};

const createSearch =
    <T, Response = T>(
        searchType: SearchType,
        componentName: SearchableComponentsName,
        { responseHandler = identity }: { responseHandler?: (response: Response, enterpriseId: string) => T[] } = {}
    ): SearchFunction =>
    (profileId, enterpriseId, term, { startIndex, count }, optionalParams) =>
    (dispatch, getState, { container }) =>
        container()
            [componentName].search(profileId, enterpriseId, term, { startIndex, count }, optionalParams)
            .then((response: Response) => responseHandler(response, enterpriseId))
            .then((result: T[]) => ({
                searchType,
                result,
                hasMore: result.length >= count,
            }));

const searchBill = createSearch(SearchType.Bill, 'billComponent');
const searchCustomer = createSearch(SearchType.Customer, 'customerComponent');
const searchEnterpriseProfile = createSearch(SearchType.EnterpriseProfile, 'enterpriseProfileComponent', {
    responseHandler: enterpriseProfileResponseHandler,
});
const searchCompany = createSearch(SearchType.Company, 'companyComponent', {
    responseHandler: companyResponseHandler,
});
const searchCities = createSearch(SearchType.City, 'cityComponent');
const searchReservation = createSearch(SearchType.Reservation, 'reservationComponent', {
    responseHandler: reservationResponseHandler,
});
const searchReservationGroup = createSearch(SearchType.ReservationGroup, 'reservationGroupComponent');
const searchTravelAgency = createSearch(SearchType.TravelAgency, 'travelAgencyComponent', {
    responseHandler: travelAgencyResponseHandler,
});
const searchResourceBlock = createSearch(SearchType.ResourceBlock, 'resourceBlockComponent');
const searchLocations = createSearch(SearchType.Locations, 'locationsComponent');
const searchProductServiceOrder = createSearch(SearchType.ProductServiceOrder, 'productServiceOrdersComponent', {
    responseHandler: productServiceOrderResponseHandler,
});

const searchTypesMap = {
    [SearchType.Bill]: searchBill,
    [SearchType.Customer]: searchCustomer,
    [SearchType.EnterpriseProfile]: searchEnterpriseProfile,
    [SearchType.Company]: searchCompany,
    [SearchType.City]: searchCities,
    [SearchType.Reservation]: searchReservation,
    [SearchType.ReservationGroup]: searchReservationGroup,
    [SearchType.Resource]: searchResources,
    [SearchType.TravelAgency]: searchTravelAgency,
    [SearchType.ResourceBlock]: searchResourceBlock,
    [SearchType.Locations]: searchLocations,
    [SearchType.Navigation]: searchNavigation,
    [SearchType.ProductServiceOrder]: searchProductServiceOrder,
};
